/**
 * Authentication Types and Interfaces
 * Type definitions for user authentication system
 */

export interface User {
  id: string; // Backend uses 'id' via toJSON transform, not '_id'
  _id?: string; // Optional for backward compatibility
  username: string;
  email: string;
  displayName?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  
  // Game statistics
  gamesPlayed: number;
  gamesWon: number;
  totalExperience: number;
  level: number;
  
  // Multi-room support
  activeRooms?: Array<{
    roomId: string;
    joinedAt: Date;
    isActive: boolean;
    gameType: string;
  }>;
  maxActiveRooms?: number;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  emailOrUsername: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  displayName?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileData {
  username?: string;
  displayName?: string;
  email?: string;
}

export interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// API Response Types
export interface APIResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string | Record<string, string>;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RefreshTokenResponse {
  tokens: AuthTokens;
}

export interface ProfileResponse {
  user: User;
}

// Error Types
export interface AuthError {
  message: string;
  field?: string;
  code?: string;
}

// Constants
export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData'
} as const;

export const AUTH_ENDPOINTS = {
  REGISTER: '/api/auth/register',
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  PROFILE: '/api/auth/profile',
  UPDATE_PROFILE: '/api/auth/profile',
  CHANGE_PASSWORD: '/api/auth/change-password',
  REFRESH_TOKEN: '/api/auth/refresh-token'
} as const;